import { Guid } from '../../../store/model/guid';
import { GetItemById, getItemByIdSelector } from '../../../store/reducers/items/itemsSelectors';
import { CompletedItemsStore, createCompletedItemsStoreFromState } from '../models/CompletedItemsStore';
import { isCompleted, ItemsCompletion } from '../models/ItemsCompletion';
import { UserProgressService } from './UserProgressService';
import {
  Hotspot,
  isContentItemType,
  isHotspot,
  isMenuItem,
  isNonSequencedFlowItem,
  isQuiz,
  isSection,
  isSequencedFlowItem,
  Item,
  MenuItem,
  NonSequencedFlowItem,
  Quiz,
  Section,
  SequencedFlowItem,
  TrackingStatus,
  TrackingStatusEnum,
} from '@ael/core-api';
import { createSelector } from 'reselect';

export type IsItemCompleted = (item: Item) => boolean;
export type Evaluate = (item: Item) => TrackingStatus;

export interface ICompletionService {
  isComplete: IsItemCompleted;
  evaluate: Evaluate;
}

/** Service responsible for indicating whether an Item was completed. */
export class CompletionService implements ICompletionService {
  public constructor(private _completedItemsStore: CompletedItemsStore, private _getItemById: GetItemById) {}

  isComplete(item: Item): boolean {
    return this.evaluate(item) === TrackingStatusEnum.Completed;
  }

  /**
   * Evaluates given item whether it is completed.
   * Used to decide what color should the indicator next to the Item be.
   */
  evaluate(item: Item): TrackingStatus {
    if (isSection(item)) {
      const userProgressService = new UserProgressService(this._completedItemsStore, this._getItemById);
      const results = userProgressService.evaluateSection(item);
      return this._convertPercentagesToTrackingStatus(results);
    }
    if (isNonSequencedFlowItem(item) || isHotspot(item)) {
      return this._evaluateItemWithContentItems(item);
    }

    if (isMenuItem(item)) {
      return this._evaluateMenuItemChildren(item);
    }

    if (isContentItemType(item)) {
      return this._completedItemsStore.isFinished(item) ? TrackingStatusEnum.Completed : TrackingStatusEnum.NotVisited;
    }

    if (isQuiz(item) || isSequencedFlowItem(item)) {
      return this._evaluateTwoStepItems(item);
    }

    return TrackingStatusEnum.NotTracked;
  }

  /*
  This is a bit hackish, but serves general purpose. We only care about item being completed or not being started
  which is represented by 0 or 100% respectively
   */
  private _convertPercentagesToTrackingStatus(results: number) {
    if (results === 0) {
      return TrackingStatusEnum.NotVisited;
    }
    if (results === 100) {
      return TrackingStatusEnum.Completed;
    }
    return TrackingStatusEnum.InProgress;
  }

  private _evaluateTwoStepItems = (item: Quiz | SequencedFlowItem): TrackingStatus => {
    if (item.contentItems.isEmpty()) {
      return TrackingStatusEnum.NotTracked;
    }
    if (this._completedItemsStore.isFinished(item)) {
      return TrackingStatusEnum.Completed;
    }
    if (this._completedItemsStore.isInProgress(item)) {
      return TrackingStatusEnum.InProgress;
    }

    return TrackingStatusEnum.NotVisited;
  };

  private _evaluateItemWithContentItems = (item: NonSequencedFlowItem | Hotspot | Section): TrackingStatus => {
    const userProgressService = new UserProgressService(this._completedItemsStore, this._getItemById);
    const results = userProgressService.evaluate(item);
    return this._convertCompletionToTrackingStatusEnum(results);
  };

  private _convertCompletionToTrackingStatusEnum = (completion: ItemsCompletion): TrackingStatus => {
    if (completion.itemsCount === 0) {
      return TrackingStatusEnum.NotTracked;
    }
    if (isCompleted(completion)) {
      return TrackingStatusEnum.Completed;
    }

    if (completion.completedItemsCount === 0) {
      return TrackingStatusEnum.NotVisited;
    }
    return TrackingStatusEnum.InProgress;
  };

  private _evaluateMenuItemChildren = (menuItem: MenuItem): TrackingStatus => {
    const noSubItems = menuItem.childItems.isEmpty();
    if (noSubItems) {
      return TrackingStatusEnum.NotTracked;
    }

    return menuItem.childItems.reduce((acc: TrackingStatus, childId: Guid) => {
      const someSubItemWasInProgress = acc === TrackingStatusEnum.InProgress;
      if (someSubItemWasInProgress) {
        return TrackingStatusEnum.InProgress;
      }

      const child = this._getItemById(childId);
      const childStatus = this.evaluate(child);
      if (childStatus === TrackingStatusEnum.InProgress) {
        return TrackingStatusEnum.InProgress;
      }

      const itsFirstItem = acc === TrackingStatusEnum.NotTracked;
      if (itsFirstItem) {
        return childStatus;
      }

      if (childStatus === TrackingStatusEnum.NotTracked) {
        return acc;
      }

      const isInProgress =
        (childStatus !== TrackingStatusEnum.Completed && acc === TrackingStatusEnum.Completed) ||
        (childStatus === TrackingStatusEnum.Completed && acc !== TrackingStatusEnum.Completed);
      if (isInProgress) {
        return TrackingStatusEnum.InProgress;
      }

      const allItemsSoFarAreCompleted =
        childStatus === TrackingStatusEnum.Completed && acc === TrackingStatusEnum.Completed;
      if (allItemsSoFarAreCompleted) {
        return TrackingStatusEnum.Completed;
      }

      const allItemsSoFarAreNotVisited =
        childStatus === TrackingStatusEnum.NotVisited && acc === TrackingStatusEnum.NotVisited;
      if (allItemsSoFarAreNotVisited) {
        return TrackingStatusEnum.NotVisited;
      }

      return acc;
    }, TrackingStatusEnum.NotTracked);
  };
}

export const createCompletionServiceSelector = createSelector(
  createCompletedItemsStoreFromState,
  getItemByIdSelector,
  (completedItemsStore, getItemById) => new CompletionService(completedItemsStore, getItemById)
);
export const evaluateItemSelector = createSelector(
  createCompletionServiceSelector,
  (completionService) => (item: Item) => completionService.evaluate(item)
);
export const isItemCompletedSelector = createSelector(
  createCompletionServiceSelector,
  (completionService) => (item: Item) => completionService.isComplete(item)
);
export const isHotspotCompletedSelector = createSelector(
  isItemCompletedSelector,
  (isItemCompleted) => (hotspot: Hotspot) => isItemCompleted(hotspot)
);
